/* eslint-disable jsx-a11y/anchor-is-valid */
/**
 * @file - React view for rendering notifications
 * @author pkatoria
 */
import React from 'react';
import MessagesPanel from './MessagesPanel';
import NotificationsPanel from './NotificationsPanel';
import TabList from './TabList';
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme';
import { ThemeProvider } from '@emotion/react';
import { ScreenReaderContent } from '@paypalcorp/pp-react-utils';
import { JsonObject } from '../../definitions/JsonObject';
import { Section } from '../../app/consumer/header/notifications_new.view';
import { User } from '../../app/consumer/header/user.transformer.types';
import {
  FPTI_EVENT_NAMES,
  fptiEventAttributes,
  getLegacyFptiBaseProps,
  getLegacyFptiLinkProps,
} from '../../app/consumer/fpti';

export function NotificationsIcon({ alt = '' }: { alt: string }) {
  const attributes = {
    alt,
    className: 'notifications-icon',
    src: 'https://www.paypalobjects.com/ui-web/icons/1-0-0/notifications.svg',
    width: '32px',
    height: '32px',
    'aria-hidden': true,
    'data-test-id': 'notifications-icon',
  };
  // eslint-disable-next-line jsx-a11y/alt-text
  return <img style={{ pointerEvents: 'none' }} {...attributes} />;
}

interface NotificationCenterProps {
  user?: User;
  correlationId?: string;
  content?: JsonObject;
  userKey?: string;
  closeLabel?: string;
  CloseIconString?: string;
}

export function getNotificationsProps() {
  const notificationCenterElement = document.querySelector('#notification-center-props');
  if (!notificationCenterElement) {
    return null;
  }
  try {
    const props = JSON.parse(notificationCenterElement.innerHTML);
    const {
      user = {},
      correlationId,
      content = {},
      userKey,
      closeLabel,
      CloseIconString,
    }: NotificationCenterProps = props;
    return {
      content,
      user,
      correlationId,
      userKey,
      closeLabel,
      CloseIconString,
    };
  } catch (error) {
    console.error(new Error('User tried to parse notification center props', { cause: error }));
    return null;
  }
}

interface NotificationPopoverContentsBodyProps {
  sections: Section[];
  messageCount: number;
  content: JsonObject;
  user: User;
  correlationId: string;
}

export function NotificationPopoverContentsBody({
  content,
  sections,
  correlationId,
  messageCount,
  user,
}: NotificationPopoverContentsBodyProps) {
  return (
    <div className="cw_popover-body" data-cwt-im-viewport>
      <NotificationsPanel content={content} correlationId={correlationId} sections={sections} />
      <MessagesPanel content={content} messageCount={messageCount} user={user} />
    </div>
  );
}

interface NotificationPopoverContentsProps {
  sections: Section[];
  notificationCount: number;
  messageCount: number;
  content: JsonObject;
  user: User;
  correlationId: string;
}

export function NotificationPopoverContents({
  sections,
  notificationCount,
  messageCount,
  content,
  user,
  correlationId,
}: NotificationPopoverContentsProps) {
  return (
    <ThemeProvider theme={PAYPAL_THEME}>
      <TabList content={content} notificationCount={notificationCount} messageCount={messageCount} />
      <div className="cw_tab-list-shadow" />
      <NotificationPopoverContentsBody
        content={content}
        sections={sections}
        correlationId={correlationId}
        messageCount={messageCount}
        user={user}
      />
    </ThemeProvider>
  );
}

interface MobileNotificationPopoverContentsProps {
  sections: Section[];
  notificationCount: number;
  messageCount: number;
  content: JsonObject;
  user: User;
  correlationId: string;
  closeLabel: string;
  CloseIconString: string;
}

export const MobileNotificationPopoverContents = ({
  sections,
  notificationCount,
  messageCount,
  content,
  user,
  correlationId,
  closeLabel,
  CloseIconString,
}: MobileNotificationPopoverContentsProps) => {
  const baseFPTIProps = getLegacyFptiBaseProps('notificationcenter');
  const clickFPTIProps = getLegacyFptiLinkProps('notificationcenter', 'dismiss-notifications-mobile');
  return (
    <ThemeProvider theme={PAYPAL_THEME}>
      <header className="vx_modal-header cw_notifications-mobile-content-header">
        <span
          className="vx_modal-dismiss_trigger"
          id="dismiss-notifications-modal__icon"
          data-test-id="dismiss-notifications-mobile"
          data-modal-id="notifications-mobile"
          {...fptiEventAttributes({
            base: baseFPTIProps,
            eventName: FPTI_EVENT_NAMES.NOTIFICATIONS_DISMISS,
            impression: true,
            click: clickFPTIProps,
          })}
        >
          {CloseIconString && <div dangerouslySetInnerHTML={{ __html: CloseIconString }} />}
          <ScreenReaderContent data-test-id="screenreader">{closeLabel}</ScreenReaderContent>
        </span>
        <NotificationsIcon alt="" />
        <TabList content={content} notificationCount={notificationCount} messageCount={messageCount} />
        <div className="cw_tab-list-shadow" />
      </header>
      <NotificationPopoverContentsBody
        content={content}
        sections={sections}
        correlationId={correlationId}
        messageCount={messageCount}
        user={user}
      />
    </ThemeProvider>
  );
};
